import { useState, useEffect } from "react";
import { useAudio } from "../hooks/useAudio";
import { STREAMS, type StreamId } from "../constants";
import type { NowPlaying, Track } from "../types";

interface StreamCardProps {
  id: StreamId;
  onNavigate: (id: StreamId) => void;
}

export function StreamCard({ id, onNavigate }: StreamCardProps) {
  const { state, selectStream } = useAudio();
  const stream = STREAMS[id];
  const isActive = state.currentStream === id && state.isPlaying;
  const [cardNowPlaying, setCardNowPlaying] = useState<NowPlaying | null>(null);
  const [artworkImages, setArtworkImages] = useState<string[]>([stream.artwork]);
  const [currentArtIndex, setCurrentArtIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchNowPlaying = () => {
      fetch(`/api/nowplaying/${id}`)
        .then((r) => r.json())
        .then((data) => {
          if (data.title) {
            setCardNowPlaying(data);
          }
        })
        .catch(() => {});
    };

    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 5000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (cardNowPlaying && stream.hasTrackList) {
      fetch(`/api/tracks/${id}`)
        .then((r) => r.json())
        .then((data) => {
          // Flatten all tracks from all albums
          const allTracks = (data.albums || []).flatMap((album: any) =>
            (album.tracks || []).map((t: any) => ({
              ...t,
              albumFolder: album.folder || "TEST-FOLDER-MISSING"
            }))
          );
          console.log('[StreamCard] Albums data:', data.albums.map((a: any) => ({
            folder: a.folder,
            streamId: a.streamId,
            trackCount: a.tracks?.length
          })));

          const currentTrack = allTracks.find((t: Track & { albumFolder: string }) => {
            if (!cardNowPlaying?.title) return false;

            // Match by album folder and track number if available
            if (cardNowPlaying.albumFolder && cardNowPlaying.track && t.albumFolder) {
              return t.albumFolder === cardNowPlaying.albumFolder &&
                     t.track === parseInt(cardNowPlaying.track);
            }

            // Fallback to title matching with album folder check
            if (cardNowPlaying.albumFolder && t.albumFolder) {
              const titleMatch = t.title.includes(cardNowPlaying.title.replace(/^\['|'\]$/g, ""));
              return titleMatch && t.albumFolder === cardNowPlaying.albumFolder;
            }

            // Last resort: title only (for streams without album folders)
            return t.title.includes(cardNowPlaying.title.replace(/^\['|'\]$/g, ""));
          });
          if (currentTrack && currentTrack.images.length > 0) {
            const albumFolder = (currentTrack as any).albumFolder;
            console.log('[StreamCard] Current track albumFolder:', albumFolder, 'images:', currentTrack.images);
            if (albumFolder) {
              // Use all images from the track's image_files array
              const images = currentTrack.images.map((img: string) =>
                `/api/artwork/${albumFolder}/${img}`
              );
              console.log('[StreamCard] Generated image URLs:', images);
              setArtworkImages(images);
              setCurrentArtIndex(0);
            } else {
              // Fallback to stream artwork if albumFolder is missing
              console.log('[StreamCard] No albumFolder, using stream artwork');
              setArtworkImages([stream.artwork]);
            }
          } else {
            // Fallback to stream artwork if no images in metadata
            setArtworkImages([stream.artwork]);
          }
        })
        .catch(() => {
          setArtworkImages([stream.artwork]);
        });
    }
  }, [cardNowPlaying?.title, id]);

  useEffect(() => {
    if (artworkImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentArtIndex((prev) => (prev + 1) % artworkImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [artworkImages.length]);

  useEffect(() => {
    if (!cardNowPlaying?.timestamp || !cardNowPlaying?.duration) {
      setProgress(0);
      return;
    }

    const updateProgress = () => {
      const now = Math.floor(Date.now() / 1000);
      const elapsed = now - cardNowPlaying.timestamp;
      const pct = Math.min((elapsed / cardNowPlaying.duration) * 100, 100);
      setProgress(pct);
    };

    updateProgress();
    const interval = setInterval(updateProgress, 1000);
    return () => clearInterval(interval);
  }, [cardNowPlaying?.timestamp, cardNowPlaying?.duration]);

  const handleClick = () => {
    if (state.currentStream === id) {
      onNavigate(id);
    } else {
      selectStream(id);
      onNavigate(id);
    }
  };

  return (
    <div
      onClick={handleClick}
      className="relative bg-[var(--bg-surface)] border border-[var(--border-subtle)] overflow-hidden cursor-pointer transition-colors duration-300"
      style={{
        borderColor: isActive ? stream.color : undefined,
        boxShadow: isActive ? `0 20px 40px -20px ${stream.color}40` : undefined,
      }}
    >
      <div className="relative aspect-square overflow-hidden">
        {artworkImages.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={stream.name}
            loading="eager"
            decoding="sync"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              const fallbackImage = id === 'jukebox' ? '/albums/jukebox.jpg' : '/swansong.city.jpg';
              if (!img.src.endsWith(fallbackImage)) {
                img.src = fallbackImage;
              }
            }}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
            style={{
              opacity: i === currentArtIndex ? 1 : 0.01,
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
            }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-surface)] via-transparent to-transparent" />
        {isActive && (
          <div
            className="absolute top-3 right-3 px-2 py-1 rounded-full font-mono text-[10px] tracking-wider animate-pulse bg-black/60 backdrop-blur-sm"
            style={{ color: stream.color }}
          >
            LIVE
          </div>
        )}
      </div>

      <div className="p-4 h-[140px] flex flex-col">
        <div
          className="font-mono text-[10px] tracking-[0.15em] uppercase mb-1"
          style={{ color: stream.color }}
        >
          {stream.genre}
        </div>
        <h3 className="font-display text-base tracking-wide mb-1 line-clamp-1 flex-shrink-0">
          {stream.name}
        </h3>

        <div className="mt-auto">
          <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-1">
            Now Playing
          </div>
          <p className="text-sm text-[var(--text-primary)] leading-tight line-clamp-1">
            {cardNowPlaying?.title
              ? cardNowPlaying.title.replace(/^\['|'\]$/g, "")
              : "Loading..."}
          </p>
          <p className="text-xs text-[var(--text-secondary)] line-clamp-1">
            {cardNowPlaying?.artist
              ? cardNowPlaying.artist.replace(/^\['|'\]$/g, "")
              : stream.description}
          </p>
        </div>
      </div>

      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{
          backgroundColor: stream.color,
          transform: isActive ? "scaleX(1)" : "scaleX(0)",
          transformOrigin: "left",
          transition: "transform 0.3s",
        }}
      />

      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[var(--border-subtle)]">
        <div
          className="h-full transition-all duration-300 ease-linear"
          style={{
            backgroundColor: stream.color,
            width: `${progress}%`,
          }}
        />
      </div>
    </div>
  );
}
